import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SCROLL_THRESHOLD = 16;

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const location = useLocation();
  const scrollRaf = useRef<number | null>(null);

  useEffect(() => {
    const update = () => {
      scrollRaf.current = null;
      const next = window.scrollY > SCROLL_THRESHOLD;
      setIsScrolled((prev) => (prev !== next ? next : prev));
    };

    const onScroll = () => {
      if (scrollRaf.current != null) return;
      scrollRaf.current = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scrollRaf.current != null) window.cancelAnimationFrame(scrollRaf.current);
    };
  }, []);

  const { t, i18n } = useTranslation('common');

  const languages = [
    { code: 'cs', label: 'CZ' },
    { code: 'sk', label: 'SK' },
  ];

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  };

  const navItems = [
    { path: '/', label: t('navbarHome'), icon: '🏠' },
    { path: '/about', label: t('navbarAbout'), icon: '👤' },
    { path: '/projects', label: t('navbarProjects'), icon: '💻' },
    { path: '/skills', label: t('navbarSkills'), icon: '🧠' },
    { path: '/friends', label: t('navbarFriends'), icon: '🤝' },
    { path: '/contact', label: t('navbarContact'), icon: '✉️' },
  ];

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 isolate transform-gpu border-b transition-[background-color,box-shadow] duration-200 ${
        isScrolled
          ? 'border-primary-500/25 shadow-lg shadow-black/20'
          : 'border-transparent'
      }`}
      style={{
        backgroundColor: isScrolled ? 'var(--app-nav-solid)' : 'var(--app-nav-soft)',
      }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-center sm:justify-between">
          <Link to="/" className="group relative hidden items-center space-x-3 sm:flex">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 text-xl font-bold text-purple-200 shadow-lg">
              <span className="relative z-10">A</span>
            </div>
            <div className="transform" />
          </Link>

          <div className="hidden items-center space-x-1 lg:flex">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={`group relative overflow-hidden rounded-lg px-4 py-2 font-semibold uppercase tracking-wide transition-colors duration-150 ${
                    isActive ? 'text-primary-300' : 'text-dark-300 hover:text-primary-400'
                  }`}
                >
                  {isActive && (
                    <span className="absolute inset-0 -z-10 rounded-lg bg-gradient-to-r from-primary-500/25 to-secondary-500/20" />
                  )}
                  <span className="relative flex items-center space-x-2">
                    <span className="text-lg">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="relative mr-4 hidden sm:block">
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const open = !isLangOpen;
                setIsLangOpen(open);
                if (open) setIsMobileMenuOpen(false);
              }}
              className="group flex items-center space-x-2 rounded-md border border-primary-500/60 bg-dark-800/70 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-purple-200 transition-colors hover:border-primary-400 hover:bg-dark-800/80 focus:outline-none focus:ring-2 focus:ring-primary-500/60"
            >
              <span className="transition-colors group-hover:text-primary-300">
                {languages.find((l) => l.code === i18n.language)?.label}
              </span>
              <span className={`text-xs transition-transform ${isLangOpen ? 'rotate-180' : ''}`}>▼</span>
            </motion.button>
            <AnimatePresence>
              {isLangOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 z-50 mt-2 w-28 space-y-1 rounded-md border border-dark-600 bg-dark-800/95 p-1 shadow-lg"
                >
                  {languages.map((lang) => (
                    <li key={lang.code}>
                      <button
                        type="button"
                        onClick={() => {
                          changeLang(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={`flex w-full items-center space-x-2 rounded-md px-3 py-2 text-left text-purple-200 transition-colors duration-150 ${
                          i18n.language === lang.code
                            ? 'bg-primary-500/20 text-primary-300'
                            : 'hover:bg-primary-500/10 hover:text-primary-300'
                        }`}
                      >
                        <span>{lang.label}</span>
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={() => {
              const open = !isMobileMenuOpen;
              setIsMobileMenuOpen(open);
              if (open) setIsLangOpen(false);
            }}
            className="text-2xl text-purple-200 focus:outline-none lg:hidden"
          >
            <span>☰</span>
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4 overflow-hidden lg:hidden"
            >
              <div className="space-y-2 rounded-xl border border-dark-600 bg-dark-800/90 p-4">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`flex items-center space-x-3 rounded-lg px-4 py-3 font-semibold uppercase tracking-wide transition-colors ${
                        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-dark-300 hover:text-primary-400'
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
